/**
 * KEJSIK landing sections behaviour.
 *
 * - Hero: GSAP ScrollTrigger scrubs the case rotation/lift and headline parallax.
 * - Reveals: [data-kj-reveal] fade up once they enter the viewport.
 * - Cards: [data-kj-tilt] tilt towards the pointer on fine-pointer devices.
 * - Promo: [data-kj-countdown] ticks to its end date, [data-kj-copy] copies a code.
 *
 * Loaded as a module from snippets/kejsik-base.liquid, so it runs once per page even
 * though every KEJSIK section references it. Re-initialises sections the theme editor
 * re-renders and tears down their animations on unload.
 */

const GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
const SCROLL_TRIGGER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

/** @type {WeakMap<Element, () => void>} cleanup callbacks per section root */
const cleanups = new WeakMap();

/** @type {Promise<any> | null} */
let gsapPromise = null;

/** @type {WeakMap<Element, Element | Window>} scroller each hero was bound to */
const heroScrollers = new WeakMap();

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') return resolve();
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true }
    );
    script.addEventListener('error', reject, { once: true });
    document.head.append(script);
  });
}

/** Loads GSAP + ScrollTrigger once and resolves with the gsap instance. */
function loadGsap() {
  if (!gsapPromise) {
    gsapPromise = (async () => {
      if (!window.gsap) await loadScript(GSAP_URL);
      if (!window.ScrollTrigger) await loadScript(SCROLL_TRIGGER_URL);
      window.gsap.registerPlugin(window.ScrollTrigger);
      window.ScrollTrigger.config({ ignoreMobileResize: true });
      return window.gsap;
    })();
  }
  return gsapPromise;
}

/**
 * Horizon can scroll inside `.page-wrapper` instead of the window, so ScrollTrigger
 * must listen to whichever ancestor actually scrolls.
 * @param {Element} el
 * @returns {Element | Window}
 */
function getScroller(el) {
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
  }
  return window;
}

/* ---------------------------------------------------------------- Hero */

/**
 * @param {HTMLElement} hero
 * @returns {Promise<(() => void) | void>}
 */
async function initHero(hero) {
  /** @type {{ viewer: any, disposed: boolean }} */
  const state = { viewer: null, disposed: false };
  const controller = new AbortController();
  initHero3d(hero, state);

  // Pointer tilts the 3D model (fine pointers only; touch keeps the scroll motion).
  if (finePointer.matches) {
    hero.addEventListener(
      'pointermove',
      (event) => {
        const rect = hero.getBoundingClientRect();
        state.viewer?.setPointer(((event.clientX - rect.left) / rect.width) * 2 - 1, ((event.clientY - rect.top) / rect.height) * 2 - 1);
      },
      { signal: controller.signal }
    );
    hero.addEventListener('pointerleave', () => state.viewer?.setPointer(0, 0), { signal: controller.signal });
  }

  const dispose = (/** @type {(() => void) | undefined} */ revert) => () => {
    state.disposed = true;
    controller.abort();
    state.viewer?.dispose();
    state.viewer = null;
    hero.classList.remove('is-3d');
    revert?.();
  };

  if (reducedMotion.matches) return dispose();

  let gsap;
  try {
    gsap = await loadGsap();
  } catch {
    return dispose(); // CDN unavailable: the hero stays static, CSS float still runs.
  }
  if (!hero.isConnected) return dispose();
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const caseEl = hero.querySelector('[data-kj-case]');
  const caseIntro = hero.querySelector('[data-kj-case-intro]');
  const card = hero.querySelector('[data-kj-card]');
  const track = hero.querySelector('.kj-hero__track') || hero;

  const scroller = getScroller(hero);
  heroScrollers.set(hero, scroller);

  const ctx = gsap.context(() => {
    const lines = gsap.utils.toArray('[data-kj-line] > span', hero);

    // Intro: headline slides up from its mask, case drops in.
    const intro = gsap.timeline({ defaults: { ease: 'expo.out' } });
    intro
      .from(lines, { yPercent: 110, duration: 1.2, stagger: 0.1 })
      .from(caseIntro, { y: 160, scale: 0.6, rotation: -25, opacity: 0, duration: 1.4 }, 0.2)
      .from('[data-kj-sticker]', { scale: 0, opacity: 0, duration: 0.8, stagger: 0.12, ease: 'back.out(2)' }, 0.7)
      .from('[data-kj-bar]', { y: 30, opacity: 0, duration: 0.9 }, 0.8);

    // Scroll: the case spins and lifts, headline lines drift apart.
    const scroll = gsap.timeline({
      defaults: { ease: 'none', duration: 1 },
      scrollTrigger: {
        trigger: track,
        scroller,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1,
        onUpdate: (self) => state.viewer?.setProgress(self.progress),
      },
    });

    scroll
      .fromTo(
        caseEl,
        { rotation: -8 },
        {
          rotation: 10,
          y: isMobile ? '-2vh' : '-22vh',
          scale: isMobile ? 1.08 : 1.22,
        },
        0
      )
      // Full flip with a little overshoot: front, back photo mid-scroll, front again.
      .fromTo(card, { rotationY: -18, transformPerspective: 1400 }, { rotationY: 378 }, 0)
      .to(
        hero.querySelectorAll('[data-kj-line="left"]'),
        { xPercent: isMobile ? -6 : -18 },
        0
      )
      .to(hero.querySelectorAll('[data-kj-line="right"]'), { xPercent: isMobile ? 6 : 18 }, 0)
      .to('[data-kj-sticker="1"]', { y: '-30vh', rotation: -30 }, 0)
      .to('[data-kj-sticker="2"]', { y: '-45vh', rotation: 25 }, 0)
      .to('[data-kj-glow]', { opacity: 0.85 }, 0)
      .to('[data-kj-bar]', { opacity: 0, y: -40, duration: 0.25 }, 0.75);
  }, hero);

  return dispose(() => ctx.revert());
}

/**
 * Loads the three.js viewer for heroes with a model; reveals it once the GLB renders.
 * @param {HTMLElement} hero
 * @param {{ viewer: any, disposed: boolean }} state
 */
async function initHero3d(hero, state) {
  const container = hero.querySelector('[data-kj-3d]');
  const { kjModel: url, kjViewerSrc: src } = hero.dataset;
  if (!(container instanceof HTMLElement) || !url || !src) return;

  // Without WebGL or when loading fails, fall back to the photo card.
  const showFallback = () => {
    hero.classList.add('is-3d-failed');
    const card = hero.querySelector('[data-kj-card]');
    if (card instanceof HTMLElement) card.hidden = false;
  };
  if (!window.WebGLRenderingContext) return showFallback();

  try {
    const { mount } = await import(src);
    const viewer = await mount(container, { url, reducedMotion: reducedMotion.matches });
    if (state.disposed || !hero.isConnected) return viewer.dispose();
    state.viewer = viewer;
    hero.classList.add('is-3d');
  } catch (error) {
    console.warn('[KEJSIK] 3D model unavailable, showing photos instead.', error);
    showFallback();
  }
}

/* ------------------------------------------------------------- Reveals */

/** @type {IntersectionObserver | null} */
let revealObserver = null;

/** @param {ParentNode} root */
function initReveals(root) {
  const items = root.querySelectorAll('[data-kj-reveal]:not(.is-in)');
  if (!items.length) return;

  if (reducedMotion.matches || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }

  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        revealObserver?.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
  );

  items.forEach((el) => revealObserver?.observe(el));
}

/* ---------------------------------------------------------------- Tilt */

/**
 * @param {ParentNode} root
 * @returns {() => void}
 */
function initTilt(root) {
  const controller = new AbortController();
  if (reducedMotion.matches || !finePointer.matches) return () => controller.abort();

  for (const card of root.querySelectorAll('[data-kj-tilt]')) {
    const inner = card.firstElementChild;
    if (!(inner instanceof HTMLElement)) continue;
    const { signal } = controller;

    card.addEventListener(
      'pointermove',
      (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        inner.style.transform = `rotateY(${x * 10}deg) rotateX(${y * -10}deg) translateY(-6px)`;
      },
      { signal }
    );
    card.addEventListener('pointerleave', () => (inner.style.transform = ''), { signal });
  }

  return () => controller.abort();
}

/* --------------------------------------------------------------- Promo */

/**
 * Parses "YYYY-MM-DD HH:MM" as local shop-visitor time.
 * @param {string} value
 */
function parseEndDate(value) {
  const normalized = value.trim().replace(' ', 'T');
  const date = new Date(normalized.length === 10 ? `${normalized}T23:59:59` : normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * @param {ParentNode} root
 * @returns {() => void}
 */
function initCountdowns(root) {
  /** @type {number[]} */
  const timers = [];

  for (const el of root.querySelectorAll('[data-kj-countdown]')) {
    if (!(el instanceof HTMLElement)) continue;
    const end = parseEndDate(el.dataset.kjCountdown || '');
    if (!end) continue;

    const units = {
      d: el.querySelector('[data-unit="d"]'),
      h: el.querySelector('[data-unit="h"]'),
      m: el.querySelector('[data-unit="m"]'),
      s: el.querySelector('[data-unit="s"]'),
    };

    const tick = () => {
      const diff = end.getTime() - Date.now();
      if (diff <= 0) {
        el.hidden = true;
        return false;
      }
      const total = Math.floor(diff / 1000);
      const values = {
        d: Math.floor(total / 86400),
        h: Math.floor((total % 86400) / 3600),
        m: Math.floor((total % 3600) / 60),
        s: total % 60,
      };
      for (const [key, node] of Object.entries(units)) {
        if (node) node.textContent = String(values[/** @type {keyof typeof values} */ (key)]).padStart(2, '0');
      }
      el.hidden = false;
      return true;
    };

    if (tick()) {
      const id = window.setInterval(() => {
        if (!tick()) window.clearInterval(id);
      }, 1000);
      timers.push(id);
    }
  }

  return () => timers.forEach((id) => window.clearInterval(id));
}

/**
 * @param {ParentNode} root
 * @returns {() => void}
 */
function initCopy(root) {
  const controller = new AbortController();

  for (const button of root.querySelectorAll('[data-kj-copy]')) {
    if (!(button instanceof HTMLElement)) continue;
    const label = button.querySelector('[data-kj-copy-label]');
    const original = label?.textContent ?? '';

    button.addEventListener(
      'click',
      async () => {
        const code = button.dataset.kjCopy || '';
        try {
          await navigator.clipboard.writeText(code);
        } catch {
          const range = document.createRange();
          const value = button.querySelector('.kj-promo__code-value');
          if (value) {
            range.selectNodeContents(value);
            window.getSelection()?.removeAllRanges();
            window.getSelection()?.addRange(range);
          }
          return;
        }
        if (label) {
          label.textContent = button.dataset.kjCopied || original;
          window.setTimeout(() => (label.textContent = original), 2000);
        }
      },
      { signal: controller.signal }
    );
  }

  return () => controller.abort();
}

/* ---------------------------------------------------------------- Boot */

/** @param {Element} section */
async function initSection(section) {
  cleanups.get(section)?.();

  const disposers = [initTilt(section), initCountdowns(section), initCopy(section)];
  initReveals(section);
  cleanups.set(section, () => disposers.forEach((dispose) => dispose()));

  const hero = section.matches('[data-kj-hero]') ? section : section.querySelector('[data-kj-hero]');
  if (hero instanceof HTMLElement) {
    const disposeHero = await initHero(hero);
    if (disposeHero) {
      cleanups.set(section, () => {
        disposers.forEach((dispose) => dispose());
        disposeHero();
      });
    }
  }

  if (section.matches('[data-kj-collection]')) {
    const disposeCollection = await initCollection(section);
    if (disposeCollection) {
      cleanups.set(section, () => {
        disposers.forEach((dispose) => dispose());
        disposeCollection();
      });
    }
  }

  if (section.matches('[data-kj-scrollfx]')) {
    const disposeFx = await initScrollFx(section);
    if (disposeFx) {
      cleanups.set(section, () => {
        disposers.forEach((dispose) => dispose());
        disposeFx();
      });
    }
  }

  if (section.matches('[data-kj-gallery]')) {
    const disposeGallery = await initGallery(section);
    if (disposeGallery) {
      cleanups.set(section, () => {
        disposers.forEach((dispose) => dispose());
        disposeGallery();
      });
    }
  }
}

/* ---------------------------------------------------------- Collection */

/**
 * Collection page motion: the hero title shrinks and fades as it scrolls away, floating
 * photos drift at different speeds, and each card grows and straightens as it enters.
 * @param {Element} section
 * @returns {Promise<(() => void) | void>}
 */
async function initCollection(section) {
  const cards = section.querySelectorAll('[data-kj-scrub]');
  if (reducedMotion.matches) return;

  let gsap;
  try {
    gsap = await loadGsap();
  } catch {
    return;
  }
  if (!section.isConnected) return;

  const scroller = getScroller(section);
  const hero = section.querySelector('[data-kj-coll-hero]');

  const ctx = gsap.context(() => {
    if (hero) {
      const heroScroll = { trigger: hero, scroller, start: 'top top', end: 'bottom top', scrub: 0.6 };
      gsap.to('[data-kj-coll-title]', { scale: 0.7, opacity: 0.2, y: '18vh', ease: 'none', scrollTrigger: heroScroll });
      for (const float of section.querySelectorAll('[data-kj-float]')) {
        const speed = Number(float instanceof HTMLElement ? float.dataset.kjFloat : 1) || 1;
        gsap.to(float, { y: () => -window.innerHeight * 0.35 * speed, scale: 0.8 + speed * 0.15, ease: 'none', scrollTrigger: { ...heroScroll } });
      }
    }

    cards.forEach((card, i) => {
      gsap.fromTo(
        card,
        { scale: 0.78, rotation: i % 2 ? 5 : -5, y: 80, opacity: 0.25 },
        {
          scale: 1,
          rotation: 0,
          y: 0,
          opacity: 1,
          ease: 'none',
          scrollTrigger: { trigger: card, scroller, start: 'top bottom', end: 'top 65%', scrub: 0.5 },
        }
      );
    });
  }, section);

  return () => ctx.revert();
}

/* ----------------------------------------------------------- Scroll FX */

/**
 * Generic scroll effects for content pages: [data-kj-fill] lines fill with color as they
 * cross the viewport, [data-kj-float] photos drift up at their own speed.
 * @param {Element} section
 * @returns {Promise<(() => void) | void>}
 */
async function initScrollFx(section) {
  const fills = section.querySelectorAll('[data-kj-fill]');
  if (reducedMotion.matches) {
    fills.forEach((el) => el instanceof HTMLElement && el.style.setProperty('--kj-fill', '100%'));
    return;
  }

  let gsap;
  try {
    gsap = await loadGsap();
  } catch {
    fills.forEach((el) => el instanceof HTMLElement && el.style.setProperty('--kj-fill', '100%'));
    return;
  }
  if (!section.isConnected) return;

  const scroller = getScroller(section);
  const ctx = gsap.context(() => {
    for (const line of fills) {
      gsap.fromTo(
        line,
        { '--kj-fill': '0%', scale: 0.92 },
        {
          '--kj-fill': '100%',
          scale: 1,
          ease: 'none',
          scrollTrigger: { trigger: line, scroller, start: 'top 85%', end: 'top 35%', scrub: 0.4 },
        }
      );
    }

    for (const float of section.querySelectorAll('[data-kj-float]')) {
      const speed = Number(float instanceof HTMLElement ? float.dataset.kjFloat : 1) || 1;
      gsap.to(float, {
        y: () => -window.innerHeight * 0.4 * speed,
        scale: 0.8 + speed * 0.15,
        ease: 'none',
        scrollTrigger: { trigger: float.parentElement, scroller, start: 'top top', end: 'bottom top', scrub: 0.6 },
      });
    }
  }, section);

  return () => ctx.revert();
}

/* ------------------------------------------------------------- Gallery */

/**
 * Drifts the photo row sideways while the section crosses the viewport (desktop only;
 * phones keep native swipe scrolling).
 * @param {Element} section
 * @returns {Promise<(() => void) | void>}
 */
async function initGallery(section) {
  if (reducedMotion.matches || window.matchMedia('(max-width: 749px)').matches) return;
  const track = section.querySelector('[data-kj-gallery-track]');
  if (!(track instanceof HTMLElement)) return;

  let gsap;
  try {
    gsap = await loadGsap();
  } catch {
    return;
  }
  if (!section.isConnected) return;

  const ctx = gsap.context(() => {
    gsap.fromTo(
      track,
      { x: () => Math.min(0, section.clientWidth - track.scrollWidth) * 0.1 },
      {
        x: () => Math.min(0, section.clientWidth - track.scrollWidth),
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          scroller: getScroller(section),
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.6,
          invalidateOnRefresh: true,
        },
      }
    );
  }, section);

  return () => ctx.revert();
}

function boot() {
  window.KEJSIK = true;
  for (const section of document.querySelectorAll('.kj')) initSection(section);

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    for (const section of target.querySelectorAll('.kj')) initSection(section);
    window.ScrollTrigger?.refresh();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    for (const section of target.querySelectorAll('.kj')) {
      cleanups.get(section)?.();
      cleanups.delete(section);
    }
  });

  window.addEventListener('load', () => window.ScrollTrigger?.refresh(), { once: true });

  // The scrolling container can switch between window and .page-wrapper with the viewport.
  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      for (const hero of document.querySelectorAll('[data-kj-hero]')) {
        if (heroScrollers.has(hero) && heroScrollers.get(hero) !== getScroller(hero)) initSection(hero);
      }
    }, 250);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
